import { useCallback, useEffect, useState } from "react";
import { Search, UserPlus, Pencil, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, StatCard, BrandButton } from "@/components/PageHeader";
import { StaffDialog, ResetDialog } from "@/components/staff/StaffDialogs";
import { useApp } from "@/context/AppContext";
import { ROLES } from "@/lib/access";
import { api, fmtDate, initials } from "@/lib/api";

const slug = (r) => r.replace(/\s+/g, "-").toLowerCase();

const Row = ({ s, me, onEdit, onReset }) => (
  <tr className={s.active ? "" : "opacity-50"} data-testid={`staff-row-${s.username}`}>
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-[#111] text-white text-xs font-bold grid place-items-center shrink-0">{initials(s.name)}</div>
        <div><div className="font-medium whitespace-nowrap">{s.name}{s.id === me && <span className="ml-2 text-[10px] font-bold uppercase text-gray-400">You</span>}</div><div className="text-xs text-gray-500">{s.email}</div></div>
      </div>
    </td>
    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{s.username}</td>
    <td className="px-4 py-3 whitespace-nowrap">{s.role}<div className="text-[11px] text-gray-500" data-testid={`staff-modules-${s.username}`}>{s.role === "Super Admin" ? "All modules" : `${(s.modules || []).length} module${(s.modules || []).length === 1 ? "" : "s"}${s.can_export === false ? " · exports off" : ""}`}</div></td>
    <td className="px-4 py-3 whitespace-nowrap text-xs">
      {!s.active ? <span className="text-gray-500">Deactivated</span> : s.must_change_password ? <span className="text-amber-700 font-semibold" data-testid={`staff-pending-${s.username}`}>Awaiting first sign-in</span> : <span className="text-emerald-700 font-semibold">Active</span>}
    </td>
    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{s.last_login ? fmtDate(s.last_login) : "Never"}</td>
    <td className="px-4 py-3 whitespace-nowrap text-right space-x-2">
      <button onClick={() => onEdit(s)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#111] hover:text-[#E60000]" data-testid={`staff-edit-${s.username}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
      <button onClick={() => onReset(s)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#E60000] hover:underline" data-testid={`staff-reset-${s.username}`}><KeyRound className="w-3.5 h-3.5" />Reset password</button>
    </td>
  </tr>
);

export default function AccessControl() {
  const { user } = useApp();
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState({ open: false, staff: null });
  const [reset, setReset] = useState(null);
  const load = useCallback(() => api.get("/staff").then((r) => setStaff(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const rows = staff.filter((s) => `${s.name} ${s.email} ${s.username} ${s.role}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="access-control-page">
      <PageHeader eyebrow="Super Admin" title="Staff Access Control" description="Add staff accounts, choose which modules each person can open, set their default password, and reset passwords when needed."
        actions={<BrandButton onClick={() => setEdit({ open: true, staff: null })} data-testid="staff-add-button"><UserPlus className="w-4 h-4" />Add staff</BrandButton>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {ROLES.map((r, i) => <StatCard key={r} label={r} value={staff.filter((s) => s.role === r && s.active).length} testId={`access-count-${slug(r)}`} delay={i * 40} />)}
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-lg">
        <div className="p-4 border-b">
          <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, username or role" className="pl-9 h-10" data-testid="access-search-input" /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="access-table">
            <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500">
              <tr>{["Staff member", "Username", "Role", "Status", "Last sign-in", ""].map((h) => <th key={h} className="text-left font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y">{rows.map((s) => <Row key={s.id} s={s} me={user?.id} onEdit={(x) => setEdit({ open: true, staff: x })} onReset={setReset} />)}</tbody>
          </table>
          {rows.length === 0 && <div className="p-10 text-center text-sm text-gray-500">No staff match your search.</div>}
        </div>
      </div>
      <StaffDialog open={edit.open} staff={edit.staff} onClose={() => setEdit({ open: false, staff: null })} onSaved={load} />
      <ResetDialog key={reset?.id} staff={reset} onClose={() => setReset(null)} onSaved={load} />
    </div>
  );
}
